import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { createRideFeeRule } from '../../api/admin';

// New ride fee rule / version (Kayd, 28.10.2026): country + currency,
// Boda-Boda or Auto, the driver's tariff, the NULA MOVE fee added on top
// (percentage of the driver's trip amount, or fixed), rounding to whole
// currency units, effective from, and the reason. Saving never changes an
// existing version.
// Only the markets Nula Move operates in (Kayd, 04.11.2026).
const CURRENCY_FOR = { KE: 'KES', US: 'USD' };
const localNow = () => { const d = new Date(Date.now() + 5 * 60000); d.setSeconds(0, 0); return new Date(d - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };

export default function RideFeeForm({ onSaved, defaultCountry = 'KE' }) {
  const [form, setForm] = useState({
    country_code: CURRENCY_FOR[defaultCountry] ? defaultCountry : 'KE', currency: CURRENCY_FOR[defaultCountry] || 'KES', vehicle_category: 'boda_boda',
    base_fare: '', per_km: '', minimum_fare: '', fee_type: 'percent', fee_value: '10', rounding: 'round_half_up',
    effective_from: localNow(), note: '',
    wait_free_seconds: '300', wait_rate_cents_per_minute: '0',
    wait_no_show_limit_seconds: '900', wait_fee_type: 'percent', wait_rounding: '',
    wait_fee_value: '0', arrival_radius_m: '250', no_show_enabled: true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Example with the entered values (6 km).
  const ex = (() => {
    const unit = (x) => { const fn = { round_half_up: Math.round, round_down: Math.floor, round_up: Math.ceil }[form.rounding] || Math.round; return fn(x); };
    const driver = unit(Math.max((Number(form.base_fare) || 0) + (Number(form.per_km) || 0) * 6, Number(form.minimum_fare) || 0));
    const fee = form.fee_type === 'percent' ? unit(driver * (Number(form.fee_value) || 0) / 100) : unit(Number(form.fee_value) || 0);
    return { driver, fee, total: driver + fee };
  })();

  const save = async (e) => {
    e.preventDefault();
    if (!form.note.trim()) { toast.error('Please enter the reason for this rule.'); return; }
    // Billing per second or per started minute must be decided explicitly.
    if (Number(form.wait_rate_cents_per_minute) > 0 && !form.wait_rounding) { toast.error('Please choose how waiting time is billed (per second or per started minute).'); return; }
    setSaving(true);
    try {
      await createRideFeeRule({ ...form, fee_value: Number(form.fee_value), effective_from: new Date(form.effective_from).toISOString() });
      toast.success('Rule saved as a new version.');
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save the rule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} style={s.wrap}>
      <h3 style={s.title}>New ride fee rule / version</h3>
      <div style={s.grid}>
        <label style={s.label}>Country
          <select style={s.input} value={form.country_code} onChange={(e) => { const c = e.target.value; setForm((f) => ({ ...f, country_code: c, currency: CURRENCY_FOR[c] })); }}>
            <option value="KE">Kenya (KE)</option><option value="US">United States (US)</option>
          </select>
        </label>
        <label style={s.label}>Currency
          <input style={s.input} value={form.currency} maxLength={3} required onChange={(e) => set('currency', e.target.value.toUpperCase())} />
        </label>
        <label style={s.label}>Vehicle category
          <select style={s.input} value={form.vehicle_category} onChange={(e) => set('vehicle_category', e.target.value)}>
            <option value="boda_boda">Boda-Boda</option><option value="auto">Auto</option>
          </select>
        </label>
      </div>
      <fieldset style={s.fs}>
        <legend style={s.legend}>Driver's trip amount (what the driver earns)</legend>
        <div style={s.grid}>
          <label style={s.label}>Base fare ({form.currency})<input style={s.input} type="number" min="0" step="0.01" value={form.base_fare} onChange={(e) => set('base_fare', e.target.value)} /></label>
          <label style={s.label}>Per km ({form.currency})<input style={s.input} type="number" min="0" step="0.01" value={form.per_km} onChange={(e) => set('per_km', e.target.value)} /></label>
          <label style={s.label}>Minimum fare ({form.currency})<input style={s.input} type="number" min="0" step="0.01" value={form.minimum_fare} onChange={(e) => set('minimum_fare', e.target.value)} /></label>
        </div>
      </fieldset>
      <fieldset style={s.fs}>
        <legend style={s.legend}>Waiting and no-show rules</legend>
        <div style={s.grid}>
          <label style={s.label}>Free wait seconds<input style={s.input} type="number" min="0" value={form.wait_free_seconds} onChange={(e) => set('wait_free_seconds', e.target.value)} /></label>
          <label style={s.label}>Waiting rate per minute ({form.currency})<input style={s.input} type="number" min="0" step="0.01" value={form.wait_rate_cents_per_minute / 100} onChange={(e) => set('wait_rate_cents_per_minute', Math.round(Number(e.target.value || 0) * 100))} /></label>
          <label style={s.label}>Waiting time billed
            <select style={s.input} value={form.wait_rounding} onChange={(e) => set('wait_rounding', e.target.value)}>
              <option value="">— please choose —</option><option value="per_second">Per second</option><option value="per_started_minute">Per started minute</option>
            </select>
          </label>
          <label style={s.label}>No-show limit seconds<input style={s.input} type="number" min="0" value={form.wait_no_show_limit_seconds} onChange={(e) => set('wait_no_show_limit_seconds', e.target.value)} /></label>
          <label style={s.label}>Arrival radius (metres)<input style={s.input} type="number" min="25" value={form.arrival_radius_m} onChange={(e) => set('arrival_radius_m', e.target.value)} /></label>
          <label style={s.label}>Waiting fee type<select style={s.input} value={form.wait_fee_type} onChange={(e) => set('wait_fee_type', e.target.value)}><option value="percent">Percentage</option><option value="fixed">Fixed</option></select></label>
          <label style={s.label}>Waiting fee value<input style={s.input} type="number" min="0" step="0.01" value={form.wait_fee_value} onChange={(e) => set('wait_fee_value', e.target.value)} /></label>
          <label style={{ ...s.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={form.no_show_enabled} onChange={(e) => set('no_show_enabled', e.target.checked)} /> Enable no-show claims</label>
        </div>
      </fieldset>
      <fieldset style={s.fs}>
        <legend style={s.legend}>NULA MOVE fee (added on top — paid by the customer)</legend>
        <div style={s.grid}>
          <label style={s.label}>Fee type
            <select style={s.input} value={form.fee_type} onChange={(e) => set('fee_type', e.target.value)}>
              <option value="percent">Percentage of the driver's trip amount</option><option value="fixed">Fixed amount per trip</option>
            </select>
          </label>
          <label style={s.label}>{form.fee_type === 'percent' ? 'Fee (%)' : `Fee (${form.currency})`}<input style={s.input} type="number" min="0" step="0.01" required value={form.fee_value} onChange={(e) => set('fee_value', e.target.value)} /></label>
          <label style={s.label}>Rounding (display and accounting)
            <select style={s.input} value={form.rounding} onChange={(e) => set('rounding', e.target.value)}>
              <option value="round_half_up">Nearest whole {form.currency}</option><option value="round_down">Down to whole {form.currency}</option><option value="round_up">Up to whole {form.currency}</option>
            </select>
          </label>
        </div>
      </fieldset>
      <div style={s.grid}>
        <label style={s.label}>Effective from<input style={s.input} type="datetime-local" required value={form.effective_from} onChange={(e) => set('effective_from', e.target.value)} /></label>
        <label style={{ ...s.label, gridColumn: 'span 2' }}>Reason (saved with the version)<input style={s.input} required maxLength={500} value={form.note} onChange={(e) => set('note', e.target.value)} /></label>
      </div>
      <div style={s.example}>Example, 6 km trip: driver {form.currency} {ex.driver} + NULA MOVE fee {form.currency} {ex.fee} = customer pays <strong>{form.currency} {ex.total}</strong>. The driver earns {form.currency} {ex.driver}.</div>
      <div><button type="submit" style={s.btn} disabled={saving}>{saving ? 'Saving…' : 'Save new version'}</button></div>
    </form>
  );
}

const s = {
  wrap: { background: '#fff', border: '1px solid #d1d5db', borderRadius: 12, padding: 16, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 10 },
  title: { margin: 0, fontSize: 16, fontWeight: 800 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 },
  fs: { border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, margin: 0, minWidth: 0 },
  legend: { fontSize: 12.5, fontWeight: 800, color: '#374151', padding: '0 4px' },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, fontWeight: 700, color: '#374151', minWidth: 0 },
  input: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13.5, background: '#fff', minHeight: 38, width: '100%', boxSizing: 'border-box' },
  example: { background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', borderRadius: 8, padding: 10, fontSize: 13.5 },
  btn: { padding: '9px 14px', borderRadius: 8, border: 'none', background: '#0B6A4A', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
};
